import React from "react";
import {
  useMsalAuthentication,
  useIsAuthenticated,
} from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import Layout from "./components/Layout";
import "./App.css";
import SoldCreatePage from "./pages/Sold/SoldCreate";
import SoldListPage from "./pages/Sold/SoldList";
import SoldEditPage from "./pages/Sold/SoldEdit";
import SoldViewPage from "./pages/Sold/SoldView";
import SpecCreatePage from "./pages/Spec/SpecCreate";
import SpecListPage from "./pages/Spec/SpecList";
import SpecEditPage from "./pages/Spec/SpecEdit";
import SpecViewPage from "./pages/Spec/SpecView";
import { Container, Box } from "@mui/material";
import { LicenseInfo } from "@mui/x-license-pro";
import { Route, Routes, Navigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { JobTypeProvider, useJobType } from "./contexts/JobTypeContext";
import Login from "./pages/Login";
import { initializeLocalStorage } from "./utils/localStorage";

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_KEY as string);

const loginRequest = {
  scopes: ["user.Read", "profile", "offline_access"],
};

// Authentication guard for protected routes
const AuthenticationGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { error } = useMsalAuthentication(InteractionType.Redirect, loginRequest);
  const isAuthenticated = useIsAuthenticated();

  React.useEffect(() => {
    if (error) {
      console.error('Authentication error:', error);
    }
  }, [error]);

  // Initialize localStorage defaults once when auth resolves.
  // No change for keys that already exist, so returning users keep their state.
  React.useEffect(() => {
    if (isAuthenticated) {
      initializeLocalStorage();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <Container sx={{ mt: (theme) => theme.spacing(5) }}>
        <Box mx={2}>
          <h5 className="card-title">Signing you in...</h5>
        </Box>
      </Container>
    );
  }

  return <>{children}</>;
};

// Protected routes (require authentication)
const ProtectedRoutes: React.FC = () => {
  const { jobType } = useJobType();

  return (
    <Routes>
      {/* Root redirects to last selected job type */}
      <Route path="/" element={<Navigate to={`/${jobType}`} replace />} />

      {/* Sold routes */}
      <Route path="/sold" element={<SoldListPage />} />
      <Route path="/sold/create" element={<SoldCreatePage />} />
      <Route path="/sold/edit/:id" element={<SoldEditPage />} />
      <Route path="/sold/view/:id" element={<SoldViewPage />} />

      {/* Spec routes */}
      <Route path="/spec" element={<SpecListPage />} />
      <Route path="/spec/create" element={<SpecCreatePage />} />
      <Route path="/spec/edit/:id" element={<SpecEditPage />} />
      <Route path="/spec/view/:id" element={<SpecViewPage />} />

      {/* Legacy route redirects */}
      <Route path="/create" element={<Navigate to="/sold/create" replace />} />
      <Route path="/edit/:id" element={<Navigate to="/sold/edit/:id" replace />} />
      <Route path="/view/:id" element={<Navigate to="/sold/view/:id" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <JobTypeProvider>
        <Layout>
          <Routes>
            {/* Public route - no authentication required */}
            <Route path="/login" element={<Login />} />
            
            {/* All other routes require authentication */}
            <Route
              path="/*"
              element={
                <AuthenticationGuard>
                  <ProtectedRoutes />
                </AuthenticationGuard>
              }
            />
          </Routes>
        </Layout>
      </JobTypeProvider>
    </LocalizationProvider>
  );
};

export default App; 